import { useState, useEffect } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { fetchCarDetail, preCreateOrder, type CarDetailItem } from "@/api/car";
import ImageCarousel from "@/components/ImageCarousel";
import { EditTripModal } from "./components/EditTripModal";
import { useUserStore } from "@/store/useUserStore";
import { resolveDetailId } from "@/utils/detailNav";

/** 登录回跳继续下单所需数据 */
interface PendingCarBooking {
  carId: number;
  tripInfo: {
    pickupTime: string;
    returnTime: string;
    pickupLocation: string;
    returnLocation: string;
  };
}

export default function CarDetailProductPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // ★ 同时支持 URL query（?carId=x）与 location.state（原先只读 state，刷新后即失效）
  const carId = resolveDetailId("carId", searchParams, location.state);
  const [car, setCar] = useState<CarDetailItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [showEditTrip, setShowEditTrip] = useState(false);
  const [bookingLoading, setBookingLoading] = useState(false);

  // 行程信息状态
  const [tripInfo, setTripInfo] = useState<{
    pickupTime: string;
    pickupLocation: string;
    returnTime: string;
    returnLocation: string;
    days: number;
  } | null>(null);

  // 确认行程
  const handleConfirmTrip = (info: typeof tripInfo) => {
    setTripInfo(info);
    setShowEditTrip(false);
  };

  useEffect(() => {
    if (!carId) {
      setError("产品ID无效，无法加载车辆详情");
      setLoading(false);
      return;
    }

    const loadCarDetail = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await fetchCarDetail(carId);
        setCar(data);
      } catch (err) {
        console.error("获取车辆详情失败:", err);
        setError("获取车辆详情失败");
      } finally {
        setLoading(false);
      }
    };

    loadCarDetail();
  }, [carId]);

  // 加载中状态
  if (loading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ background: "#F8F9FC" }}
      >
        <div className="text-center">
          <div className="w-10 h-10 border-3 border-gray-200 border-t-blue-600 rounded-full animate-spin mx-auto mb-3" />
          <span className="text-sm text-gray-500">加载中...</span>
        </div>
      </div>
    );
  }

  // 错误状态
  if (error || !car) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ background: "#F8F9FC" }}
      >
        <div className="text-center px-5">
          <i className="ri-error-warning-line text-5xl text-gray-300 mb-3" />
          <p className="text-sm text-gray-500 mb-4">{error || "车辆不存在"}</p>
          <button
            onClick={() => navigate(-1)}
            className="px-6 py-2 rounded-full text-sm font-medium text-white cursor-pointer"
            style={{ background: "#1E3A8A" }}
          >
            返回上一页
          </button>
        </div>
      </div>
    );
  }

  // 处理图片列表
  const images = car.images?.length
    ? car.images
    : car.cover_url
      ? [car.cover_url]
      : [];

  // 处理车辆特点
  const featuresList =
    typeof car.features === "string"
      ? car.features.split("|").filter(Boolean)
      : [];

  return (
    <div
      className="min-h-screen pb-[calc(env(safe-area-inset-bottom,0px)+112px)]"
      style={{
        background: "#F8F9FC",
        fontFamily: "'Inter','SF Pro Text',sans-serif",
      }}
    >
      {/* Image Carousel */}
      <ImageCarousel images={images} height={240} />

      {/* Back Button */}
      <button
        onClick={() => navigate(-1)}
        className="fixed top-4 left-4 w-9 h-9 flex items-center justify-center rounded-full cursor-pointer z-30"
        style={{ background: "rgba(0,0,0,0.4)" }}
      >
        <i className="ri-arrow-left-line text-white" style={{ fontSize: 20 }} />
      </button>

      {/* Basic info */}
      <div className="bg-white px-5 py-4">
        <div className="flex items-start justify-between gap-2">
          <div>
            <h1 className="text-xl font-bold text-gray-900">{car.title}</h1>
            {car.subtitle && (
              <p className="text-sm text-gray-500 mt-1">{car.subtitle}</p>
            )}
            <div className="flex items-center gap-3 mt-2 text-sm text-gray-500">
              <span>
                <i className="ri-user-line mr-1" style={{ fontSize: 14 }} />
                {car.seats_text}
              </span>
              <span>
                <i
                  className="ri-settings-2-line mr-1"
                  style={{ fontSize: 14 }}
                />
                {car.gearbox_text}
              </span>
              {car.license_plate && (
                <span>
                  <i className="ri-car-line mr-1" style={{ fontSize: 14 }} />
                  {car.license_plate}
                </span>
              )}
            </div>
          </div>
          <div className="text-right">
            <div
              className="font-bold"
              style={{ color: "#EF4444", fontSize: 24 }}
            >
              ¥{car.daily_price}
            </div>
            <div className="text-xs text-gray-400">元/天</div>
          </div>
        </div>
      </div>

      {/* Rating & Reviews */}
      <div className="bg-white mt-2 px-5 py-3">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-1">
            <i
              className="ri-star-fill text-yellow-400"
              style={{ fontSize: 14 }}
            />
            <span className="text-sm font-semibold text-gray-800">
              {car.rating}
            </span>
          </div>
          <span className="text-sm text-gray-400">|</span>
          <span className="text-sm text-gray-500">
            {car.reviews_count}条评价
          </span>
          <span className="text-sm text-gray-400">|</span>
          <span className="text-sm text-gray-500">{car.car_type}</span>
        </div>
      </div>

      {/* Trip info */}
      <div className="bg-white mt-2 px-5 py-4">
        {tripInfo ? (
          <>
            <div className="flex items-center justify-between mb-3">
              <div className="text-sm font-semibold text-gray-800">
                租车行程
              </div>
              <button
                onClick={() => setShowEditTrip(true)}
                className="flex items-center gap-1 text-xs font-medium cursor-pointer whitespace-nowrap px-3 py-1.5 rounded-full"
                style={{ background: "#EFF6FF", color: "#1E3A8A" }}
              >
                <i className="ri-edit-2-line" style={{ fontSize: 12 }} />
                修改
              </button>
            </div>
            <div
              className="rounded-2xl p-4"
              style={{ background: "#F8F9FC", border: "1px solid #E5E7EB" }}
            >
              <div className="flex items-center gap-3">
                <div className="flex flex-col items-center gap-5 flex-shrink-0">
                  <div
                    className="w-2.5 h-2.5 rounded-full"
                    style={{ background: "#1E3A8A" }}
                  />
                  <div
                    className="w-px flex-1"
                    style={{ background: "#CBD5E1", height: 30 }}
                  />
                  <div
                    className="w-2.5 h-2.5 rounded-full border-2"
                    style={{ borderColor: "#1E3A8A" }}
                  />
                </div>
                <div className="flex-1 min-w-0 space-y-3">
                  <div>
                    <div className="text-xs text-gray-400">取车</div>
                    <div className="text-sm font-semibold text-gray-800">
                      {tripInfo.pickupTime}
                    </div>
                    <div className="text-xs text-gray-500 truncate">
                      {tripInfo.pickupLocation}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-400">还车</div>
                    <div className="text-sm font-semibold text-gray-800">
                      {tripInfo.returnTime}
                    </div>
                    <div className="text-xs text-gray-500 truncate">
                      {tripInfo.returnLocation}
                    </div>
                  </div>
                </div>
                <div className="text-center flex-shrink-0 ">
                  <div
                    className="text-2xl font-bold"
                    style={{ color: "#1E3A8A" }}
                  >
                    {tripInfo.days}
                  </div>
                  <div className="text-xs text-gray-400">天</div>
                </div>
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="text-sm font-semibold text-gray-800 mb-3">
              租车行程
            </div>
            <button
              onClick={() => setShowEditTrip(true)}
              className="w-full flex items-center justify-center gap-2 py-4 rounded-xl border-2 border-dashed"
              style={{ borderColor: "#CBD5E1", color: "#1E3A8A" }}
            >
              <i className="ri-add-line" style={{ fontSize: 18 }} />
              <span className="text-sm font-medium">添加租车行程</span>
            </button>
          </>
        )}
      </div>

      {/* Rental rules */}
      <div className="bg-white mt-2 px-5 py-4">
        <div className="text-sm font-semibold text-gray-800 mb-3">租车须知</div>
        <div className="space-y-2">
          {[
            {
              icon: "ri-shield-check-line",
              label: "保险",
              value: car.insurance_info || "已包含",
            },
            {
              icon: "ri-coins-line",
              label: "押金",
              value: car.is_no_deposit
                ? car.deposit_type_text
                : `${car.deposit_type_text} ¥${car.deposit_amount ?? "—"}`,
            },
            {
              icon: "ri-briefcase-2-line",
              label: "服务类型",
              value: car.service_type_text,
            },
            {
              icon: "ri-calendar-check-line",
              label: "租期",
              value: `${car.min_order_days}-${car.max_order_days}天`,
            },
          ].map((item) => (
            <div
              key={item.label}
              className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0"
            >
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <i
                  className={item.icon}
                  style={{ fontSize: 16, color: "#9CA3AF" }}
                />
                {item.label}
              </div>
              <span className="text-sm text-gray-700 font-medium">
                {item.value}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Features */}
      {featuresList.length > 0 && (
        <div className="bg-white mt-2 px-5 py-4">
          <div className="text-sm font-semibold text-gray-800 mb-3">
            车辆特点
          </div>
          <div className="flex flex-wrap gap-2">
            {featuresList.map((feature, i) => (
              <span
                key={i}
                className="text-xs px-2.5 py-1 rounded-full"
                style={{ background: "#EFF6FF", color: "#1E3A8A" }}
              >
                {feature}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Description */}
      {car.description && (
        <div className="bg-white mt-2 px-5 py-4">
          <div className="text-sm font-semibold text-gray-800 mb-2">
            详细说明
          </div>
          <div className="text-xs text-gray-500 leading-relaxed">
            {car.description}
          </div>
        </div>
      )}

      {/* Cancel policy */}
      {car.cancel_policy.length > 0 && (
        <div className="bg-white mt-2 px-5 py-4 mb-4">
          <div className="text-sm font-semibold text-gray-800 mb-2">
            取消政策
          </div>
          <div className="text-xs text-gray-500 leading-relaxed">
            {car.cancel_policy}
          </div>
        </div>
      )}

      {showEditTrip && (
        <EditTripModal
          initialData={tripInfo}
          onClose={() => setShowEditTrip(false)}
          onConfirm={handleConfirmTrip}
        />
      )}

      {/* Bottom bar */}
      <div
        className="fixed bottom-0 left-0 right-0 bg-white px-5 pt-4 z-40"
        style={{ boxShadow: "0 -4px 20px rgba(0,0,0,0.06)", paddingTop: "16px", paddingBottom: "calc(16px + env(safe-area-inset-bottom, 0px))" }}
      >
        <div className="flex items-center justify-between">
          <div>
            <span className="text-xs text-gray-400">租金</span>
            <div className="flex items-baseline gap-1">
              <span
                className="font-bold"
                style={{ color: "#EF4444", fontSize: 26 }}
              >
                ¥{car.daily_price}
              </span>
              <span className="text-xs text-gray-400">/天</span>
            </div>
          </div>
          <button
            onClick={async () => {
              if (!tripInfo) {
                setShowEditTrip(true);
                return;
              }
              if (!car || !car.id || bookingLoading) {
                console.warn("[立即预订] 被拦截:", { car, carId: car?.id, bookingLoading });
                return;
              }

              // ── 登录检查 ──────────────────────────────────────────
              const isLoggedIn = useUserStore.getState().isLoggedIn;
              if (!isLoggedIn) {
                // 未登录：保存当前行程信息，跳转微信授权，登录后回跳继续下单
                const pending: PendingCarBooking = {
                  carId: car.id,
                  tripInfo,
                };
                sessionStorage.setItem(
                  "pending_car_booking",
                  JSON.stringify(pending),
                );
                navigate("/wx-auth?redirect=/order-form");
                return;
              }
              // ── 已登录：正常预创建订单 ─────────────────────────────
              setBookingLoading(true);
              console.log("[立即预订] car_id:", car.id, "car:", car);
              try {
                const result = await preCreateOrder({
                  car_id: car.id,
                  pickup_date: tripInfo.pickupTime.split(" ")[0],
                  pickup_time: tripInfo.pickupTime.split(" ")[1] || "10:00",
                  return_date: tripInfo.returnTime.split(" ")[0],
                  return_time: tripInfo.returnTime.split(" ")[1] || "10:00",
                  pickup_location: tripInfo.pickupLocation,
                  return_location: tripInfo.returnLocation,
                });
                navigate("/order-form", {
                  state: { type: "car", orderId: result.order_id },
                });
              } catch (err) {
                console.error("预创建订单失败:", err);
              } finally {
                setBookingLoading(false);
              }
            }}
            disabled={bookingLoading}
            className="px-8 py-3 rounded-full text-sm font-bold text-white cursor-pointer whitespace-nowrap transition-all active:scale-95 disabled:opacity-50"
            style={{ background: "#1E3A8A" }}
          >
            {bookingLoading ? "处理中..." : "立即预订"}
          </button>
        </div>
      </div>
    </div>
  );
}
